import React, {useState} from "react";
import {
    CheckOutlined,
    ExclamationCircleOutlined,
    StopOutlined,
    DownOutlined
} from "@ant-design/icons";
import {
    Alert,
    Button,
    Dropdown,
    Input,
    Menu,
    Popconfirm,
    Row,
    Select,
    Space,
    message,
    Tooltip
} from "antd";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import {useTranslation} from "react-i18next";
import {ActionButton} from "./CourierOnboardingActionButtons.styles";
import "./CourierOnboardingActionButtons.css";
import {activationSubSteps, personalCompanySetupStatusEnums} from "constants/constants";
import {CourierZoneManuallyUpdateModal} from "components/_defaultComponents/CourierZoneManuallyUpdateModal/CourierZoneManuallyUpdateModal";
import {SendToEvaluationModal} from "./SendToEvaluationModal";
import {TerminateCourierModal} from "./TerminateCourierModal";
import {StartLeaveModal} from "./StartLeaveModal";

const MIN_REJECT_REASON_LENGTH = 15;
export const CourierOnboardingActionButtons = ({
    courier,
    courierLoading = true,
    reloadCourier,
    isTabActive
}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [updateCourierStatusLoading, setUpdateCourierStatusLoading] = useState(false);
    const [onboardingDocumentRejectReason, setOnboardingDocumentRejectReason] = useState("");
    const [blockReason, setBlockReason] = useState("");
    const [applicationRejectReason, setApplicationRejectReason] = useState("");
    const [onboardingRejectNote, setOnboardingRejectNote] = useState("");
    const [selectedActivationSubStep, setSelectedActivationSubStep] = useState("");
    const [selectedReason, setSelectedReason] = useState("");
    const [showZoneManuallyUpdateModal, setShowZoneManuallyUpdateModal] = useState(false);

    // Lifecycle modal visibility states
    const [showEvaluationModal, setShowEvaluationModal] = useState(false);
    const [showTerminateModal, setShowTerminateModal] = useState(false);
    const [showLeaveModal, setShowLeaveModal] = useState(false);

    const [documentsProgressResultLoading, documentsProgressResult] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getDocumentsProgress.bind(null, {
            courierId: courier?._id?.toString()
        }),
        dependencies: [isTabActive, courierLoading],
        initialCall: isTabActive,
        condition: isTabActive && !courierLoading
    });

    const completeOnboardingDocumentPhase = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.completeOnboardingDocumentPhase({
                courierId: courier?._id.toString()
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setUpdateCourierStatusLoading(false);
        }
    };

    const completeTrainingPhase = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.completeTrainingPhase({
                courierId: courier?._id.toString()
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setUpdateCourierStatusLoading(false);
        }
    };

    const completeShiftWaitingPhase = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.completeShiftWaitingPhase({
                courierId: courier?._id.toString()
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setUpdateCourierStatusLoading(false);
        }
    };

    const rejectOnboardingDocumentVerificationStatus = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.rejectOnboardingDocumentVerificationStatus({
                courierId: courier?._id?.toString(),
                onboardingRejectReason: onboardingDocumentRejectReason || ""
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setOnboardingDocumentRejectReason("");
            setUpdateCourierStatusLoading(false);
        }
    };

    const blockCourier = async () => {
        if (!blockReason) {
            message.error("Lütfen kara listeye alma sebebini giriniz.");
            return;
        }
        if (blockReason?.trim()?.length < MIN_REJECT_REASON_LENGTH) {
            message.error(
                `Lütfen kara listeye alma sebebini en az ${MIN_REJECT_REASON_LENGTH} karakter olacak şekilde giriniz.`
            );
            return;
        }
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.blockCourier({
                courierId: courier?._id?.toString(),
                reason: blockReason || "",
                selectedReason
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setBlockReason("");
            setUpdateCourierStatusLoading(false);
        }
    };

    const unblockCourier = async () => {
        if (!blockReason) {
            message.error("Lütfen kara listeden çıkarma sebebini giriniz.");
            return;
        }
        if (blockReason?.trim()?.length < MIN_REJECT_REASON_LENGTH) {
            message.error(
                `Lütfen kara listeden çıkarma sebebini en az ${MIN_REJECT_REASON_LENGTH} karakter olacak şekilde giriniz.`
            );
            return;
        }
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.unblockCourier({
                courierId: courier?._id?.toString(),
                reason: blockReason || ""
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setBlockReason("");
            setUpdateCourierStatusLoading(false);
        }
    };

    const rejectCourierApplication = async () => {
        if (!selectedActivationSubStep) {
            message.error("Lütfen işe alım durumunu seçiniz.");
            return;
        }
        if (!applicationRejectReason) {
            message.error("Lütfen olumsuz olarak işaretleme sebebini giriniz.");
            return;
        }
        if (applicationRejectReason?.trim()?.length < MIN_REJECT_REASON_LENGTH) {
            message.error(
                `Lütfen olumsuz olarak işaretleme sebebini en az ${MIN_REJECT_REASON_LENGTH} karakter olacak şekilde giriniz.`
            );
            return;
        }
        if (selectedActivationSubStep === "outOfProcess" && !onboardingRejectNote?.trim()) {
            message.error("Lütfen süreçten çekilmesi nedenini giriniz.");
            return;
        }
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.rejectCourierApplication({
                courierId: courier?._id?.toString(),
                reason: applicationRejectReason || "",
                selectedActivationSubStep: selectedActivationSubStep,
                onboardingRejectNote:
                    selectedActivationSubStep === "outOfProcess"
                        ? onboardingRejectNote.trim()
                        : undefined
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setApplicationRejectReason("");
            setOnboardingRejectNote("");
            setSelectedActivationSubStep("");
            setUpdateCourierStatusLoading(false);
        }
    };

    const isRejectApplicationConfirmDisabled = () => {
        if (!selectedActivationSubStep || !applicationRejectReason) {
            return true;
        }
        if (selectedActivationSubStep === "outOfProcess" && !onboardingRejectNote.trim()) {
            return true;
        }
        return false;
    };

    const undoCourierRejection = async () => {
        if (!applicationRejectReason) {
            message.error("Lütfen olumsuz işaretleme geri alma sebebini giriniz.");
            return;
        }
        if (applicationRejectReason?.trim()?.length < MIN_REJECT_REASON_LENGTH) {
            message.error(
                `Lütfen olumsuz işaretleme geri alma sebebini en az ${MIN_REJECT_REASON_LENGTH} karakter olacak şekilde giriniz.`
            );
            return;
        }
        setUpdateCourierStatusLoading(true);
        try {
            await stores.courierStore.undoCourierRejection({
                courierId: courier?._id?.toString(),
                reason: applicationRejectReason || ""
            });
            reloadCourier();
        } catch (e) {
            console.error(e);
        } finally {
            setApplicationRejectReason("");
            setUpdateCourierStatusLoading(false);
        }
    };

    /* if (courierLoading || !courier) {
        return <div>Yükleniyor...</div>;
    } */
    const checkPermission = key => {
        const activationStep = courier?.activationStep;
        const activationSubStep = courier?.activationSubStep;
        const activationPhase = courier?.activationPhase;
        const isGetirCourier = ["Getir", "getir"].includes(courier?.company?.companyName);

        const isTerminatedOrApprovedLifecycle =
            activationStep === "approved" &&
            ["pendingEvaluation", "pendingTermination"].includes(activationSubStep);

        // Eğitim artık tek subStep (onTheJobTraining) + trainingStatus ile yönetildiği için
        // "Eğitimi Tamamla" butonu trainingStatus bazlı gösterilir. Eski kayıtlar için
        // activationSubStep=trainingCompletion kontrolü de korunur.
        const isTrainingCompletedAndPendingApproval =
            activationSubStep === "trainingCompletion" ||
            courier?.trainingStatus === "pendingApproval";

        const permissions = {
            viewApproveOnboardingDocumentVerification:
                !stores.meStore.checkRole("isCompanyUser") && activationStep === "candidate",
            viewCompleteTrainingPhaseButton:
                stores.meStore.checkRole("completeTrainingPhase") &&
                activationStep === "candidate" &&
                activationPhase === "training" &&
                isTrainingCompletedAndPendingApproval,
            viewCompleteShiftWaitingPhaseButton:
                !stores.meStore.checkRole("isCompanyUser") &&
                activationStep === "approved" &&
                activationPhase === "shiftWaiting" &&
                activationSubStep === "shiftWaiting" &&
                isGetirCourier,
            viewOnboardingDocumentRejectButton:
                !stores.meStore.checkRole("isCompanyUser") && !documentsProgressResultLoading,

            // "Ret Aksiyonları" approved veya terminated lifecycle durumlarında gizlenir
            viewBlockButtonGroup:
                !stores.meStore.checkRole("isCompanyUser") &&
                activationStep !== "approved" &&
                activationStep !== "terminated",

            viewBlackListButton:
                !stores.meStore.checkRole("isCompanyUser") &&
                !documentsProgressResultLoading &&
                courier?.activationStep !== "blocked",

            viewRemoveFromBlackListButton:
                !stores.meStore.checkRole("isCompanyUser") &&
                !documentsProgressResultLoading &&
                courier?.activationStep === "blocked",

            viewOnboardingDocumentApproveButton: !stores.meStore.checkRole("isCompanyUser"),

            viewCourierApplicationRejectButton:
                !stores.meStore.checkRole("isCompanyUser") &&
                !documentsProgressResultLoading &&
                courier?.activationStep !== "rejected" &&
                courier?.activationStep !== "approved",

            viewUndoCourierRejectionButton:
                !stores.meStore.checkRole("isCompanyUser") &&
                !documentsProgressResultLoading &&
                courier?.activationStep === "rejected",

            blockCourier:
                !stores.meStore.checkRole("isCompanyUser") &&
                !documentsProgressResultLoading &&
                courier?.activationStep !== "blocked",
            rejectCourierApplication:
                !stores.meStore.checkRole("isCompanyUser") &&
                !documentsProgressResultLoading &&
                courier?.activationStep !== "blocked" &&
                courier?.activationStep !== "rejected" &&
                courier?.activationStep !== "approved",

            completeTrainingPhase:
                stores.meStore.checkRole("completeTrainingPhase") &&
                activationStep === "candidate" &&
                activationPhase === "training" &&
                isTrainingCompletedAndPendingApproval,
            completeShiftWaitingPhase:
                !stores.meStore.checkRole("isCompanyUser") &&
                activationStep === "approved" &&
                activationPhase === "shiftWaiting" &&
                activationSubStep === "shiftWaiting" &&
                isGetirCourier,
            approveOnboardingDocumentVerification:
                !stores.meStore.checkRole("isCompanyUser") &&
                activationSubStep === "documentsInReview" &&
                !documentsProgressResultLoading &&
                documentsProgressResult?.isReadyToApprove,
            rejectOnboardingDocumentVerification:
                !stores.meStore.checkRole("isCompanyUser") &&
                [
                    "documentsInReview",
                    "projectWaiting",
                    "onTheJobTraining",
                    "trainingCompletion"
                ].includes(activationSubStep) &&
                !documentsProgressResultLoading &&
                documentsProgressResult?.isReadyToRejectDocuments,

            // Lifecycle butonları
            viewSendToEvaluationButton:
                stores.meStore.checkRole("sendCourierToEvaluation") &&
                activationStep === "approved" &&
                ["approved", "shiftWaiting"].includes(activationSubStep),

            viewEvaluationActionButton:
                stores.meStore.checkRole("cancelCourierEvaluation") &&
                activationStep === "approved" &&
                activationSubStep === "pendingEvaluation",

            viewTerminateButton:
                stores.meStore.checkRole("terminateCourier") &&
                activationStep === "approved" &&
                activationSubStep === "pendingTermination",

            viewStartLeaveButton:
                stores.meStore.checkRole("startCourierLeave") &&
                activationStep === "approved" &&
                courier?.metaData?.lastLeave?.status !== "active" &&
                activationSubStep !== "onLeave",

            viewEndLeaveButton:
                stores.meStore.checkRole("endCourierLeave") &&
                activationStep === "approved" &&
                courier?.metaData?.lastLeave?.status === "active"
        };

        // Alt kullanıcı (Acente Temsilcisi) yetkisi: yaşam döngüsü butonları "lifecycle",
        // diğer işe alım butonları "onboarding" yetkisine bağlıdır.
        const isLifecycleKey = [
            "viewSendToEvaluationButton",
            "viewEvaluationActionButton",
            "viewTerminateButton",
            "viewStartLeaveButton",
            "viewEndLeaveButton"
        ].includes(key);
        return (
            (permissions?.[key] &&
                stores.meStore.checkPermission(
                    isLifecycleKey ? "couriers.lifecycle" : "couriers.onboarding"
                )) ||
            false
        );
    };

    const renderBlackListButton = () => (
        <Popconfirm
            placement="bottomLeft"
            icon={null}
            title={
                <>
                    <h3>
                        <b>Kara Listeye Al</b>
                    </h3>
                    <p>Kuryeyi kara listeye almak istediğinize emin misiniz? </p>
                    <p>
                        Bu işlem yapıldıktan sonra; kurye ya da Vigo Yetkilisi, ilgili kurye kaydı
                        üzerinde herhangi bir işlem yapamayacaktır.
                    </p>
                    Kara Listeye Alınma Sebebi:
                    <Select
                        placeholder="Kurye başvurusunu olumsuz olarak işaretleme nedenini seçiniz."
                        style={{width: "100%", zIndex: 1062}}
                        value={selectedReason}
                        dropdownStyle={{zIndex: 9999}}
                        onChange={value => {
                            setSelectedReason(value);
                            if (value !== "outOfProcess") {
                                setOnboardingRejectNote("");
                            }
                            setBlockReason(
                                t(
                                    activationSubSteps.find(subStep => subStep.value === value)
                                        ?.translation_key,
                                    {
                                        ns: "activationSubSteps"
                                    }
                                )
                            ) || "";
                        }}
                    >
                        {activationSubSteps
                            .filter(subStep => subStep.isBlockedSubStep)
                            .map(subStep => (
                                <Select.Option
                                    key={subStep.value}
                                    value={subStep.value}
                                    label={t(subStep.translation_key, {
                                        ns: "activationSubSteps"
                                    })}
                                    style={{zIndex: 1063}}
                                >
                                    {t(subStep.translation_key, {
                                        ns: "activationSubSteps"
                                    })}
                                </Select.Option>
                            ))}
                    </Select>
                </>
            }
            overlayStyle={{
                width: "300px"
            }}
            onConfirm={() => blockCourier()}
            okText={"Onayla"}
            cancelText={"İptal"}
            okButtonProps={{disabled: !blockReason || !selectedReason}}
            disabled={!checkPermission("blockCourier")}
        >
            <Button
                loading={updateCourierStatusLoading}
                type="primary "
                style={{
                    backgroundColor: "#000000",
                    borderColor: "#000000",
                    color: "white",
                    opacity: !checkPermission("blockCourier") ? 0.5 : 1
                }}
                //icon={<StopOutlined />}
                disabled={!checkPermission("blockCourier")}
            >
                {courier?.activationStep === "blocked" ? "Kara Listeye Alınmış" : "Kara Listeye Al"}
            </Button>
        </Popconfirm>
    );
    const renderRemoveFromBlackListButton = () => (
        <Popconfirm
            placement="bottomLeft"
            icon={null}
            title={
                <>
                    <h3>
                        <b>Kara Listeden Çıkart</b>
                    </h3>
                    <p>Kuryeyi kara listeden çıkarmak istediğinize emin misiniz? </p>
                    <p>
                        Bu işlem yapıldıktan sonra; Vigo Yetkilileri, ilgili kurye kaydı üzerinde
                        işlem yapabilir duruma gelecektir.
                    </p>
                    <p>Kurye, kaldığı yerden işlemlerine devam edebilecektir.</p>
                    <p>Lütfen kuryenin neden kara listeden çıkarıldığını belirtin.</p>
                    <Alert
                        style={{padding: "5px"}}
                        message={
                            <span style={{fontWeight: "bold", fontSize: "14px"}}>
                                Kara Listeye Alınma Sebebi :
                            </span>
                        }
                        description={courier?.onboardingRejectReason || ""}
                        type="warning"
                    />
                    <Input.TextArea
                        placeholder="Kuryenin kara listeden çıkarılma nedenini giriniz."
                        style={{marginTop: "10px"}}
                        value={blockReason}
                        onChange={e => {
                            setBlockReason(e.target.value);
                        }}
                        rows={4}
                    />
                </>
            }
            overlayStyle={{
                width: "300px"
            }}
            onConfirm={() => unblockCourier()}
            okText={"Onayla"}
            cancelText={"İptal"}
            okButtonProps={{disabled: !blockReason}}
            disabled={!checkPermission("viewRemoveFromBlackListButton")}
        >
            <Button
                loading={updateCourierStatusLoading}
                type="primary "
                style={{
                    opacity: !checkPermission("viewRemoveFromBlackListButton") ? 0.5 : 1
                }}
                disabled={!checkPermission("viewRemoveFromBlackListButton")}
            >
                {courier?.activationStep === "blocked"
                    ? "Kara Listeden Çıkarma"
                    : "Kara Listeye Alınmış"}
            </Button>
        </Popconfirm>
    );
    const renderApplicationRejectButton = () => (
        <Popconfirm
            zIndex={1}
            placement="bottomLeft"
            icon={null}
            title={
                <>
                    <h3>
                        <b>Olumsuz</b>
                    </h3>
                    <p>
                        Kuryenin başvurusunu olumsuz olarak işaretlemek istediğinize emin misiniz?{" "}
                    </p>
                    <p>Bu işlemi yaptığınızda; kurye kaydı, olumsuz olarak işaretlenecektir.</p>
                    <p>
                        Kurye, tekrar giriş yaptığında ya da belgelerini işe alımcı onayına
                        gönderdiğinde olumsuz olarak işaretlenme durumu kalkacaktır.
                    </p>
                    Olumsuza Alınma Sebebi:
                    <Select
                        placeholder="Kurye başvurusunu olumsuz olarak işaretleme nedenini seçiniz."
                        style={{width: "100%", zIndex: 1062}}
                        value={selectedActivationSubStep}
                        onChange={value => {
                            setSelectedActivationSubStep(value);
                            if (value !== "outOfProcess") {
                                setOnboardingRejectNote("");
                            }
                            setApplicationRejectReason(
                                t(
                                    activationSubSteps.find(subStep => subStep.value === value)
                                        ?.translation_key,
                                    {
                                        ns: "activationSubSteps"
                                    }
                                )
                            ) || "";
                        }}
                    >
                        {activationSubSteps
                            .filter(subStep => subStep.isRejectSubStep)
                            .map(subStep => (
                                <Select.Option
                                    key={subStep.value}
                                    value={subStep.value}
                                    label={t(subStep.translation_key, {
                                        ns: "activationSubSteps"
                                    })}
                                    style={{zIndex: 1063}}
                                >
                                    {t(subStep.translation_key, {
                                        ns: "activationSubSteps"
                                    })}
                                </Select.Option>
                            ))}
                    </Select>
                    {selectedActivationSubStep === "outOfProcess" ? (
                        <div className="application-reject-withdrawal-note">
                            Süreçten Çekilme Notu:
                            <Input.TextArea
                                placeholder="Süreçten çekilme notunu giriniz."
                                style={{width: "100%", marginTop: 8}}
                                value={onboardingRejectNote}
                                onChange={e => setOnboardingRejectNote(e.target.value)}
                                maxLength={120}
                                showCount
                                rows={3}
                            />
                        </div>
                    ) : null}
                </>
            }
            overlayClassName="application-reject-popconfirm"
            overlayStyle={{
                width: "360px"
            }}
            onConfirm={() => rejectCourierApplication()}
            onVisibleChange={() => {
                setApplicationRejectReason("");
                setOnboardingRejectNote("");
                setSelectedActivationSubStep("");
            }}
            okText={"Onayla"}
            cancelText={"İptal"}
            okButtonProps={{
                disabled:
                    !checkPermission("rejectCourierApplication") ||
                    isRejectApplicationConfirmDisabled()
            }}
            disabled={!checkPermission("rejectCourierApplication")}
        >
            <Button
                loading={updateCourierStatusLoading}
                type="primary "
                style={{
                    backgroundColor: "#a52b2b",
                    borderColor: "#a52b2b",
                    color: "white",
                    opacity: !checkPermission("rejectCourierApplication") ? 0.5 : 1
                }}
                //icon={<StopOutlined />}
                disabled={!checkPermission("rejectCourierApplication")}
            >
                {courier?.activationStep === "rejected" ? "Olumsuz İşaretlenmiş" : "Olumsuz"}
            </Button>
        </Popconfirm>
    );
    const renderUndoCourierRejectionButton = () => (
        <Popconfirm
            placement="bottomLeft"
            icon={null}
            title={
                <>
                    <h3>
                        <b>Olumsuz İşaretlenmesini Geri Al</b>
                    </h3>
                    <p>
                        Kurye başvurusunun olumsuz olarak işaretlenmesini geri almak istediğinize
                        emin misiniz?{" "}
                    </p>
                    <p>
                        Bu işlemi yaptığınızda; kurye kaydının, olumsuz olarak işaretlenme durumu
                        kalkacaktır.
                    </p>
                    <p>
                        Lütfen kurye başvurusunun olumsuz olarak işaretlenmesini geri alma nedenini
                        belirtin.
                    </p>
                    <Alert
                        style={{padding: "5px"}}
                        message={
                            <span style={{fontWeight: "bold", fontSize: "14px"}}>
                                Olumsuz İşaretlenme Sebebi :
                            </span>
                        }
                        description={courier?.onboardingRejectReason || ""}
                        type="warning"
                    />
                    <Input.TextArea
                        placeholder="Kurye başvurusunun olumsuz olarak işaretlenmesini geri alma nedenini giriniz."
                        style={{marginTop: "10px"}}
                        value={applicationRejectReason}
                        onChange={e => {
                            setApplicationRejectReason(e.target.value);
                        }}
                        rows={4}
                    />
                </>
            }
            overlayStyle={{
                width: "300px"
            }}
            onConfirm={() => undoCourierRejection()}
            okText={"Onayla"}
            cancelText={"İptal"}
            okButtonProps={{disabled: !applicationRejectReason}}
            disabled={!checkPermission("viewUndoCourierRejectionButton")}
        >
            <Button
                loading={updateCourierStatusLoading}
                type="primary "
                style={{
                    /*  backgroundColor: "#a52b2b",
                        borderColor: "#a52b2b",
                    color: "white", */
                    opacity: !checkPermission("viewUndoCourierRejectionButton") ? 0.5 : 1
                }}
                //icon={<StopOutlined />}
                disabled={!checkPermission("viewUndoCourierRejectionButton")}
            >
                {courier?.activationStep === "rejected"
                    ? "Olumsuz İşaretlenmesini Geri Al"
                    : "Olumsuz İşaretlenmemiş"}
            </Button>
        </Popconfirm>
    );
    const renderBlockButtonGroup = () => (
        <Dropdown
            overlay={
                <Menu>
                    {checkPermission("viewCourierApplicationRejectButton") && (
                        <Menu.Item>{renderApplicationRejectButton()}</Menu.Item>
                    )}
                    {checkPermission("viewUndoCourierRejectionButton") && (
                        <Menu.Item>{renderUndoCourierRejectionButton()}</Menu.Item>
                    )}
                    {checkPermission("viewBlackListButton") && (
                        <Menu.Item>{renderBlackListButton()}</Menu.Item>
                    )}
                    {checkPermission("viewRemoveFromBlackListButton") && (
                        <Menu.Item>{renderRemoveFromBlackListButton()}</Menu.Item>
                    )}
                </Menu>
            }
            trigger={"click"}
        >
            <ActionButton
                style={{
                    backgroundColor: "#a52b2b",
                    borderColor: "#a52b2b",
                    color: "white",
                    margin: "10px 0"
                }}
                icon={<StopOutlined />}
            >
                Ret Aksiyonları
            </ActionButton>
        </Dropdown>
    );
    const renderRejectButton = () => (
        <Popconfirm
            placement="bottomLeft"
            icon={null}
            title={
                <>
                    <h4>
                        <b>Belge/Bilgi Güncelleme Talebi</b>
                    </h4>
                    <p>Kuryeden, belge/bilgi güncelleme istediğinize emin misiniz? </p>
                    <p>
                        Bu işlemi yaptığınızda;
                        <br /> kurye, belge ve bilgilerini tekrardan güncelleyebilecektir.
                    </p>
                    <b>
                        Kurye, tekrardan döküman kontrolü talep edene kadar işlem yapamayacaksınız.
                    </b>
                </>
            }
            overlayStyle={{
                width: "300px"
            }}
            onConfirm={() => rejectOnboardingDocumentVerificationStatus()}
            okText={"Onayla"}
            cancelText={"İptal"}
            disabled={!checkPermission("rejectOnboardingDocumentVerification")}
            trigger={"click"}
        >
            <ActionButton
                loading={updateCourierStatusLoading}
                type="default"
                icon={<ExclamationCircleOutlined />}
                disabled={!checkPermission("rejectOnboardingDocumentVerification")}
                style={{
                    margin: "10px 0"
                }}
            >
                Belge/Bilgi Güncelleme İste
            </ActionButton>
        </Popconfirm>
    );

    const renderApproveButton = () => {
        const isPersonalCompanySetupIncomplete =
            courier?.personalCompanySetupInfo?.status !== personalCompanySetupStatusEnums.COMPLETED;

        const tooltipTitle = isPersonalCompanySetupIncomplete
            ? "Şahıs şirketi bilgileri tamamlanmadan kurye aktif edilemez."
            : checkPermission("approveOnboardingDocumentVerification")
            ? ""
            : "Tüm bilgileri onaylamak için, yüklenen tüm belgeler onaylanmış durumda olmalıdır.";

        return (
            <Tooltip title={tooltipTitle}>
                <Popconfirm
                    placement="bottomLeft"
                    icon={null}
                    title={
                        <>
                            Kuryeye ait belgelerin ve bilgilerin doğru olduğundan emin olun.
                            <br />
                            <br /> Kuryeye, belge kontrol onayı verildikten sonra; kurye proje
                            seçimi adımına geçecektir.
                            <br />
                            <br /> <b>Bu işlem, şu an için geri alınamaz.</b>
                        </>
                    }
                    overlayStyle={{
                        width: "300px"
                    }}
                    onConfirm={completeOnboardingDocumentPhase}
                    okText={"Onayla"}
                    cancelText={"İptal"}
                    disabled={!checkPermission("approveOnboardingDocumentVerification")}
                >
                    <ActionButton
                        loading={updateCourierStatusLoading}
                        type="primary"
                        disabled={!checkPermission("approveOnboardingDocumentVerification")}
                        style={{margin: "10px 0"}}
                    >
                        <CheckOutlined /> Tüm Bilgileri Onayla
                    </ActionButton>
                </Popconfirm>
            </Tooltip>
        );
    };
    const renderCompleteTrainingPhaseButton = () => {
        const tooltipTitle = "Eğitimi Tamamla";

        return (
            <Tooltip title={tooltipTitle}>
                <Popconfirm
                    placement="bottomLeft"
                    icon={null}
                    title={<>Kurye eğitim süreci tamamlanacaktır. Emin misiniz?</>}
                    overlayStyle={{
                        width: "300px"
                    }}
                    onConfirm={completeTrainingPhase}
                    okText={"Onayla"}
                    cancelText={"İptal"}
                    disabled={!checkPermission("completeTrainingPhase")}
                >
                    <ActionButton
                        loading={updateCourierStatusLoading}
                        type="primary"
                        disabled={!checkPermission("completeTrainingPhase")}
                        style={{margin: "10px 0"}}
                    >
                        <CheckOutlined /> Eğitimi Tamamla
                    </ActionButton>
                </Popconfirm>
            </Tooltip>
        );
    };

    const renderCompleteShiftWaitingPhaseButton = () => {
        const tooltipTitle = "İşe Alım Tamamla";

        return (
            <Tooltip title={tooltipTitle}>
                <Popconfirm
                    placement="bottomLeft"
                    icon={null}
                    title={<>Kurye işe alım süreci tamamlanacaktır. Emin misiniz?</>}
                    overlayStyle={{
                        width: "300px"
                    }}
                    onConfirm={completeShiftWaitingPhase}
                    okText={"Onayla"}
                    cancelText={"İptal"}
                    disabled={!checkPermission("completeShiftWaitingPhase")}
                >
                    <ActionButton
                        loading={updateCourierStatusLoading}
                        type="primary"
                        disabled={!checkPermission("completeShiftWaitingPhase")}
                        style={{margin: "10px 0"}}
                    >
                        <CheckOutlined /> İşe Alım Tamamla
                    </ActionButton>
                </Popconfirm>
            </Tooltip>
        );
    };

    const handleCancelEvaluation = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            const result = await stores.courierStore.cancelEvaluation(courier._id.toString());
            if (result.success) {
                reloadCourier();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setUpdateCourierStatusLoading(false);
        }
    };

    const handleSendToTermination = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            const result = await stores.courierStore.sendToTermination(courier._id.toString());
            if (result.success) {
                reloadCourier();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setUpdateCourierStatusLoading(false);
        }
    };

    const handleEndLeave = async () => {
        setUpdateCourierStatusLoading(true);
        try {
            const result = await stores.courierStore.endLeave(courier._id.toString());
            if (result.success) {
                reloadCourier();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setUpdateCourierStatusLoading(false);
        }
    };

    const renderEvaluationActionDropdown = () => (
        <Dropdown
            overlay={
                <Menu>
                    <Menu.Item>
                        <Popconfirm
                            title="Değerlendirme iptal edilecek ve kurye önceki statüsüne alınacak. Emin misiniz?"
                            onConfirm={handleCancelEvaluation}
                            okText="Evet"
                            cancelText="İptal"
                        >
                            <span>Değerlendirme İptal</span>
                        </Popconfirm>
                    </Menu.Item>
                    <Menu.Item>
                        <Popconfirm
                            title="Kurye işten çıkarma bekliyor statüsüne alınacak. Emin misiniz?"
                            onConfirm={handleSendToTermination}
                            okText="Evet"
                            cancelText="İptal"
                        >
                            <span>İşten Çıkarmaya Gönder</span>
                        </Popconfirm>
                    </Menu.Item>
                </Menu>
            }
            trigger={["click"]}
        >
            <ActionButton
                style={{
                    backgroundColor: "#fa8c16",
                    borderColor: "#fa8c16",
                    color: "white",
                    margin: "10px 0"
                }}
                loading={updateCourierStatusLoading}
            >
                <Space>
                    Değerlendirme
                    <DownOutlined />
                </Space>
            </ActionButton>
        </Dropdown>
    );

    return (
        <Row style={{rowGap: "8px", columnGap: "8px"}}>
            {checkPermission("viewBlockButtonGroup") && renderBlockButtonGroup()}
            {checkPermission("viewApproveOnboardingDocumentVerification") ? (
                <>
                    {checkPermission("viewOnboardingDocumentRejectButton") && renderRejectButton()}
                    {checkPermission("viewOnboardingDocumentApproveButton") &&
                        renderApproveButton()}
                </>
            ) : (
                <> </>
            )}
            {checkPermission("viewCompleteTrainingPhaseButton") &&
                renderCompleteTrainingPhaseButton()}
            {checkPermission("viewCompleteShiftWaitingPhaseButton") &&
                renderCompleteShiftWaitingPhaseButton()}

            {/* Lifecycle butonları */}
            {checkPermission("viewSendToEvaluationButton") && (
                <ActionButton
                    style={{
                        backgroundColor: "#cf1322",
                        borderColor: "#cf1322",
                        color: "white",
                        margin: "10px 0"
                    }}
                    onClick={() => setShowEvaluationModal(true)}
                >
                    Değerlendirmeye Gönder
                </ActionButton>
            )}

            {checkPermission("viewEvaluationActionButton") && renderEvaluationActionDropdown()}

            {checkPermission("viewTerminateButton") && (
                <ActionButton
                    style={{
                        backgroundColor: "#cf1322",
                        borderColor: "#cf1322",
                        color: "white",
                        margin: "10px 0"
                    }}
                    onClick={() => setShowTerminateModal(true)}
                >
                    İşten Çıkar
                </ActionButton>
            )}

            {checkPermission("viewStartLeaveButton") && (
                <ActionButton
                    style={{
                        backgroundColor: "orange",
                        borderColor: "orange",
                        color: "white",
                        margin: "10px 0"
                    }}
                    onClick={() => setShowLeaveModal(true)}
                >
                    İzin
                </ActionButton>
            )}

            {checkPermission("viewEndLeaveButton") && (
                <Popconfirm
                    title="Kurye iznini sonlandırmak istediğinize emin misiniz? Önceki statüsüne alınacaktır."
                    onConfirm={handleEndLeave}
                    okText="Evet"
                    cancelText="İptal"
                >
                    <ActionButton
                        style={{
                            backgroundColor: "orange",
                            borderColor: "orange",
                            color: "white",
                            margin: "10px 0"
                        }}
                        loading={updateCourierStatusLoading}
                    >
                        İzni Sonlandır
                    </ActionButton>
                </Popconfirm>
            )}

            <CourierZoneManuallyUpdateModal
                courierId={courier?._id?.toString()}
                visibility={showZoneManuallyUpdateModal}
                onClose={() => setShowZoneManuallyUpdateModal(false)}
                onComplete={() => {
                    reloadCourier();
                }}
            />

            <SendToEvaluationModal
                visible={showEvaluationModal}
                onClose={() => setShowEvaluationModal(false)}
                courierId={courier?._id?.toString()}
                onSuccess={reloadCourier}
            />

            <TerminateCourierModal
                visible={showTerminateModal}
                onClose={() => setShowTerminateModal(false)}
                courierId={courier?._id?.toString()}
                onSuccess={reloadCourier}
            />

            <StartLeaveModal
                visible={showLeaveModal}
                onClose={() => setShowLeaveModal(false)}
                courierId={courier?._id?.toString()}
                onSuccess={reloadCourier}
            />
        </Row>
    );
};
